import React, { useState, useEffect } from 'react';

const TargetSelector = ({ selectedTarget, onTargetChange }) => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadTargets = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('[TargetSelector] Loading targets from API');
        
        const response = await fetch('http://localhost:3002/api/targets');
        
        if (!response.ok) {
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }
        
        const result = await response.json();
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to load targets');
        }
        
        const availableTargets = result.data.targets.map(target => ({
          id: target.id,
          name: target.name,
          displayName: target.displayName,
          url: target.url,
          hasData: target.hasData,
          status: target.status,
          frameworks: target.frameworks,
          lastScanned: target.lastScanned
        }));
        
        console.log('[TargetSelector] Loaded targets:', availableTargets);
        setTargets(availableTargets);
        
        // If no target is selected, select the first available target
        if (!selectedTarget && availableTargets.length > 0) {
          const defaultTarget = availableTargets.find(t => t.id === 5) || availableTargets[0];
          console.log('[TargetSelector] Setting default target:', defaultTarget);
          onTargetChange(defaultTarget);
        }
        
      } catch (error) {
        console.error('[TargetSelector] Error loading targets:', error);
        setError(error.message);
        
        // Fallback to hardcoded targets if API fails
        const fallbackTargets = [
          { 
            id: 5, 
            name: 'www.gsa.gov', 
            displayName: 'GSA - General Services Administration', 
            url: 'www.gsa.gov',
            hasData: true, 
            status: 'active', 
            frameworks: 6, 
            lastScanned: 'September 03, 2025' 
          },
          { 
            id: 27, 
            name: 'www.whitehouse.gov', 
            displayName: 'White House', 
            url: 'www.whitehouse.gov',
            hasData: true, 
            status: 'active', 
            frameworks: 6, 
            lastScanned: 'September 03, 2025' 
          }
        ];
        
        console.log('[TargetSelector] Using fallback targets');
        setTargets(fallbackTargets);
        
        if (!selectedTarget && fallbackTargets.length > 0) {
          const defaultTarget = fallbackTargets.find(t => t.id === 5) || fallbackTargets[0];
          onTargetChange(defaultTarget);
        }
      } finally {
        setLoading(false);
      }
    };

    loadTargets();
  }, [selectedTarget, onTargetChange]);

  const handleTargetSelect = (targetId) => {
    const target = targets.find(t => t.id === parseInt(targetId));
    if (target && target.id !== selectedTarget?.id) {
      console.log('[TargetSelector] Target selected:', target);
      onTargetChange(target);
    }
  };

  const getTargetIcon = (targetName) => {
    if (targetName.includes('gsa') || targetName.includes('GSA')) return '🏛️';
    if (targetName.includes('whitehouse') || targetName.includes('White House')) return '🏛️';
    if (targetName.includes('gov')) return '🏛️';
    return '🌐';
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'active': return '🟢';
      case 'compliant': return '✅';
      case 'partially_compliant': return '⚠️';
      case 'non_compliant': return '❌';
      default: return '⏳';
    }
  };

  if (loading) {
    return (
      <div className="target-selector loading">
        <div className="selector-label">
          <span className="selector-icon">🎯</span>
          Loading available targets...
        </div>
        <div className="loading-spinner">
          <div className="spinner small"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="target-selector error">
        <div className="selector-label">
          <span className="selector-icon">⚠️</span>
          Error loading targets: {error}
        </div>
        <div className="error-note">
          Using cached target data. Some features may be limited.
        </div>
      </div>
    );
  }

  return (
    <div className="target-selector">
      <div className="selector-header">
        <div className="selector-label">
          <span className="selector-icon">🎯</span>
          <span className="label-text">Target Website:</span>
          <span className="target-count">({targets.length} targets available)</span>
        </div>
      </div>
      
      <div className="selector-dropdown">
        <select 
          value={selectedTarget?.id || ''} 
          onChange={(e) => handleTargetSelect(e.target.value)}
          className="target-select"
        >
          {targets.map(target => (
            <option key={target.id} value={target.id}>
              {getTargetIcon(target.displayName)} {target.displayName} {getStatusIcon(target.status)} ({target.frameworks} frameworks)
            </option>
          ))}
        </select>
      </div>
      
      {selectedTarget && (
        <div className="target-info">
          <div className="target-details">
            <div className="target-primary">
              <span className="target-icon">{getTargetIcon(selectedTarget.displayName)}</span>
              <span className="target-name">{selectedTarget.displayName}</span>
              <span className="target-status">{getStatusIcon(selectedTarget.status)}</span>
            </div>
            <div className="target-meta">
              <span className="target-url">{selectedTarget.name}</span>
              <span className="target-frameworks">{selectedTarget.frameworks} frameworks available</span>
              <span className="target-scan-date">Last scanned: {selectedTarget.lastScanned}</span>
            </div>
          </div>
          <div className="target-actions">
            <button 
              onClick={() => window.location.reload()} 
              className="refresh-btn"
              title="Refresh compliance data"
            >
              🔄
            </button>
          </div>
        </div>
      )}
      
      <div className="selector-note">
        <small>
          💡 Select different targets to compare compliance across federal websites
        </small>
      </div>
    </div>
  );
};

export default TargetSelector;